import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckRounded } from "@mui/icons-material";
import { Navigate, useNavigate } from "react-router-dom";
import { EXIT_OFFER, formatPrice } from "../../data/pricingPlans";
import { authClient } from "../../lib/auth-client";
import { useQueryClient } from "@tanstack/react-query";
import {
  PaywallPackage,
  PaywallPackages,
  StoreProductPricing,
  getOfferingPackage,
  isIntroOfferEligible,
  purchaseProduct,
  restorePurchases,
} from "../../lib/revenueCat";
import { hapticSelection, hapticSuccess } from "../../lib/haptics";
import {
  isNative,
  nativePlatform,
  onAppResume,
  setBottomEdgeDeferred,
} from "../../lib/platform";
import { logAppsFlyerEvent } from "../../lib/appsflyer";
import { ExitOfferSheet } from "./ExitOfferSheet";
import { useMembership } from "../../contexts/MembershipContext";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useStoreProductPricing } from "../../hooks/useStoreProductPricing";
import { useLogout } from "../../hooks/useLogout";
import { usePaywallPackages } from "./usePaywallPackages";
import { getCompletedOnboardingAnswers } from "../Native/onboarding/onboardingStorage";
import { paywallCopy } from "../Native/onboarding/onboardingSteps";
import { heroRise } from "../Chat/chatMotion";
import { AuroraGlow } from "../auth/AuroraGlow";

/**
 * The native app's paywall. Rendered standalone (full screen, own safe-area
 * handling) by the hard membership gate for every signed-in non-member, and
 * by the /start-free-trial route on native. Purchases go through the store
 * payment sheet via RevenueCat — never the Lemon Squeezy web checkout (App
 * Store guideline 3.1.1); entitlements land via the RevenueCat webhook, so
 * success routes to /membership-success which polls for the claim flip.
 *
 * It sells the two standard packages of RevenueCat's CURRENT offering
 * ($rc_monthly and $rc_annual, both Pro, both with a free trial): every
 * figure on screen (prices, per-month equivalent, saving, trial length) is
 * read from the store, so the payment sheet can never contradict the page.
 *
 * When the onboarding quiz was completed on this install, the header copy is
 * personalized from its answers; otherwise it falls back to generic copy.
 */

/**
 * The paywall's real responsive axis is HEIGHT, not width: every iPhone sits
 * far below MUI's `sm` breakpoint, but 667pt (SE) to 932pt (Pro Max) is a lot
 * of vertical slack to either waste or run out of. The roomier plan-row scale
 * and the feature detail lines are progressive enhancements on tall screens.
 */
const TALL_SCREEN = "@media (min-height: 780px)";
const SHORT_SCREEN = "@media (max-height: 700px)";

/**
 * Native sells Pro standalone, so the web tier's "Everything in Basic" line
 * has nothing to point at — what you actually get is spelled out instead.
 *
 * The figures are the ones the product ships: 25 years is MAX_ANNUAL_LIMIT on
 * the financial charts, and "tens of thousands" of calls is what the transcript
 * viewer and AI summaries draw on from FMP (two years of FMP's calls, the
 * retired search corpus, was ~48.5k, so it is NOT "hundreds of thousands").
 *
 * Copy follows the news-service humaniser rules (packages/news-service/src/ai/
 * humaniser/prompt.ts): no em-dashes, no reflexive tricolons, no filler
 * intensifiers, and deliberately uneven line lengths.
 */
const NATIVE_PRO_FEATURES = [
  {
    title: "25 years of financial history",
    detail: "Income, balance sheet, cash flow, ratios. Every quarter.",
  },
  {
    title: "Tens of thousands of earnings calls",
    detail: "Summarised, so you skip the fluff.",
  },
  // The report quota rides along here rather than taking its own row: it is a
  // quota, not a capability, and the heading above it already establishes the
  // AI. `4` must track USAGE_TIERS.pro.reportsPerWeek on the API.
  {
    title: "AI that reads the filings for you",
    detail: "Ask anything about any public company. Plus 4 in-depth reports a week.",
  },
  // `10` must track SCREENER_MAX_SAVED_SCREENS.pro on the API.
  {
    title: "Unlimited stock screening",
    detail:
      "Filter thousands of stocks on the numbers you pick. Save up to 10 screens, with daily alerts for new matches.",
  },
  // Closes the stack so the four rows above don't read as the whole product.
  // The "more" promise sits in the TITLE because that is the line people
  // actually scan; the detail names real surfaces so it proves the claim
  // instead of asserting it, and survives when details hide on short screens.
  {
    title: "And everything else in the app",
    detail: "Insider trades, dividends, projections, watchlists.",
  },
];

type PaywallPackageKey = keyof PaywallPackages;

/**
 * Browser-only stand-ins so the paywall can be designed on web, where
 * RevenueCat doesn't run. Never shown on a device: there the store figures
 * render, or the unavailable state does.
 */
const DESIGN_TIME_PACKAGES: PaywallPackages = {
  monthly: {
    identifier: "$rc_monthly",
    productId: "design_time_monthly",
    price: 9.99,
    priceString: "£9.99",
    currencyCode: "GBP",
    freeTrialDays: 7,
  },
  annual: {
    identifier: "$rc_annual",
    productId: "design_time_annual",
    price: 74.99,
    priceString: "£74.99",
    currencyCode: "GBP",
    freeTrialDays: 7,
  },
};

/** Saving of a yearly charge against paying monthly for twelve months. */
const annualSavingPercent = (monthlyPrice: number, annualPrice: number): number =>
  Math.round((1 - annualPrice / (monthlyPrice * 12)) * 100);

const NativePaywall = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { isMembershipActive } = useMembership();
  const { capture } = useAnalytics();
  const { mutate: logout } = useLogout();

  // Yearly is PRESELECTED: it is the retention product, and the per-month
  // framing plus the saving badge exist to make it the obvious choice.
  const [selectedKey, setSelectedKey] = useState<PaywallPackageKey>("annual");
  const [busy, setBusy] = useState<"purchase" | "restore" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data: storePricing } = useStoreProductPricing();
  const {
    data: livePackages,
    isLoading: packagesLoading,
    refetch: refetchPackages,
  } = usePaywallPackages();

  const packages: PaywallPackages =
    livePackages ?? (isNative() ? { monthly: null, annual: null } : DESIGN_TIME_PACKAGES);
  const { monthly, annual } = packages;
  const selected: PaywallPackage | null =
    (selectedKey === "annual" ? annual : monthly) ?? annual ?? monthly;

  const onboardingAnswers = getCompletedOnboardingAnswers();
  const header = paywallCopy(onboardingAnswers);

  useEffect(() => {
    capture("pricing_page_viewed", {
      source: "native_paywall",
      personalized: !!onboardingAnswers,
    });
    logAppsFlyerEvent("af_content_view", {
      af_content_type: "paywall",
      af_content_id: "native_paywall",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capture]);

  // Exit-intent offer, two triggers: the deferred bottom-edge swipe (the
  // EdgeGuard native class delivers the FIRST home-swipe to the WebView while
  // the paywall is up; the second always exits) and app resume (background on
  // the paywall, come back). The handler is called through a ref so its
  // closure always reads fresh state; `busy` suppresses the resume that fires
  // when the App Store payment sheet itself closes. Dismissing the sheet is
  // NOT final (Ben, 2026-08-18): it rises again on the next exit attempt —
  // "once" belongs to the PRICE, which Apple enforces via intro-offer
  // eligibility (one per Apple ID per subscription group), not to the sheet.
  // Every early exit reports an `exit_offer_gate` reason to PostHog, because
  // a silently hidden sheet is undiagnosable from the dashboard otherwise.
  const [exitOfferOpen, setExitOfferOpen] = useState(false);
  const [exitOfferProductId, setExitOfferProductId] = useState<string | null>(
    null,
  );
  const queryClient = useQueryClient();
  const tryShowExitOffer = useRef<(trigger: "resume" | "swipe") => void>(
    () => {},
  );
  tryShowExitOffer.current = (trigger) => {
    if (exitOfferOpen || busy !== null) return;
    void (async () => {
      const gate = (reason: string) =>
        capture("exit_offer_gate", { trigger, reason });
      const session = await authClient.getSession();
      const userId = session?.data?.user?.id;
      if (!userId) {
        gate("no_user");
        return;
      }
      // The RC offering decides WHICH product the sheet sells — swapping the
      // package in the dashboard re-targets installed builds with no release.
      // The sheet's wording ("your first year") only fits an annual package,
      // so anything else in the offering keeps it hidden.
      const offer = await getOfferingPackage(userId, EXIT_OFFER.offeringId).catch(
        () => null,
      );
      if (!offer) {
        gate("no_product");
        return;
      }
      if (!offer.isAnnual) {
        gate("not_annual");
        return;
      }
      const { productId } = offer;
      let offerPricing = storePricing?.[productId];
      if (!offerPricing?.introPrice) {
        // Recovery pass: the pricing query fetches ONCE per session (1h
        // staleTime, retry: false, global refetchOnWindowFocus: false), so a
        // fetch that failed or ran before the RC offering existed would
        // otherwise make the sheet impossible until an app relaunch.
        await queryClient.refetchQueries({
          queryKey: ["storeProductPricing"],
        });
        offerPricing = queryClient
          .getQueriesData<Record<string, StoreProductPricing>>({
            queryKey: ["storeProductPricing"],
          })
          .map(([, data]) => data?.[productId])
          .find(Boolean);
      }
      // No store intro price, no sheet: a displayed discount the payment
      // sheet wouldn't honour is worse than no offer. A zero-price intro is a
      // free trial, not the first-year discount this sheet sells.
      if (!offerPricing?.introPrice || offerPricing.introPrice.price <= 0) {
        gate("no_intro_price");
        return;
      }
      // The saving is quoted against the monthly package; without its store
      // price there is no honest percentage to show.
      if (!monthly) {
        gate("no_monthly_price");
        return;
      }
      // A returning subscriber who already redeemed an intro in the group
      // would be charged the full price regardless of what we display — never
      // show them the discount. Errors stay permissive: the intro was present
      // in store data, and the payment sheet always states the real charge.
      const eligible = await isIntroOfferEligible(userId, productId).catch(
        () => true,
      );
      if (!eligible) {
        gate("ineligible");
        return;
      }
      setExitOfferProductId(productId);
      setExitOfferOpen(true);
      hapticSelection();
      capture("exit_offer_shown", { trigger, product_id: productId });
    })();
  };
  useEffect(() => onAppResume(() => tryShowExitOffer.current("resume")), []);

  // Arm the bottom-edge deferral while the paywall is on screen, and watch
  // for the deferred swipe the system then delivers: a touch starting in the
  // home-gesture band (below all content — the column's bottom padding keeps
  // real controls clear of it) that moves meaningfully upward. iOS versions
  // where deferral doesn't hold simply background the app as before, and the
  // resume trigger covers them.
  useEffect(() => {
    if (nativePlatform() !== "ios") return;
    void setBottomEdgeDeferred(true);
    let armedY: number | null = null;
    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      armedY =
        touch && touch.clientY > window.innerHeight - 24 ? touch.clientY : null;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (armedY === null) return;
      const touch = event.touches[0];
      if (touch && armedY - touch.clientY > 30) {
        armedY = null;
        tryShowExitOffer.current("swipe");
      }
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      void setBottomEdgeDeferred(false);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  if (isMembershipActive) {
    return <Navigate to="/dashboard" replace />;
  }

  // The exit offer renders exclusively from store data, for whichever product
  // the trigger resolved from the RC offering (see EXIT_OFFER).
  const offerStorePricing = exitOfferProductId
    ? storePricing?.[exitOfferProductId]
    : undefined;
  const offerIntro = offerStorePricing?.introPrice ?? null;

  // Divided down from the store price rather than the store's own per-month
  // string, which RevenueCat rounds down (79.99/12 showed as 6.66, not 6.67).
  const perMonthLabel = (pkg: PaywallPackage): string =>
    formatPrice(pkg.price / 12, pkg.currencyCode);

  const savingPercent =
    monthly && annual ? annualSavingPercent(monthly.price, annual.price) : 0;

  const trialDays = selected?.freeTrialDays ?? null;
  const storeName = nativePlatform() === "android" ? "Google Play" : "App Store";
  const renewalTerm = selected === annual ? "a year" : "a month";

  const requireUserId = async (): Promise<string | null> => {
    const session = await authClient.getSession();
    if (!session?.data?.session) {
      window.location.href = "/sign-in?reason=session_expired";
      return null;
    }
    return session.data.user.id;
  };

  const startPurchase = async (
    productId: string | undefined,
    planType: string,
  ) => {
    setBusy("purchase");
    setError(null);
    try {
      const userId = await requireUserId();
      if (!userId) return;

      if (!productId) {
        throw new Error("This plan is not available in the app yet");
      }

      capture("checkout_started", {
        plan_type: planType,
        product_id: productId,
        platform: "native",
      });
      logAppsFlyerEvent("af_initiated_checkout", {
        af_content_id: productId,
        af_content_type: "subscription",
      });

      const outcome = await purchaseProduct(userId, productId);
      if (outcome === "cancelled") {
        setBusy(null);
        return;
      }

      // Drives the SKAdNetwork conversion value, which can only be written
      // on-device — RevenueCat's server-side event can't reach it. Deliberately
      // NOT af_purchase and deliberately carrying no revenue: store revenue
      // reaches AppsFlyer once, from RevenueCat's integration.
      logAppsFlyerEvent("purchase_completed_cv", { af_content_id: productId });

      hapticSuccess();
      navigate("/membership-success");
    } catch (purchaseError) {
      const errorMessage =
        purchaseError instanceof Error
          ? purchaseError.message
          : "Failed to start the purchase";
      setError(errorMessage);
      capture("checkout_failed", {
        plan_type: planType,
        error_message: errorMessage,
        platform: "native",
      });
      setBusy(null);
    }
  };

  const handlePurchase = () =>
    startPurchase(
      selected?.productId,
      selected === annual ? "pro_annual" : "pro_monthly",
    );

  const handleAcceptExitOffer = () => {
    capture("exit_offer_accepted", { product_id: exitOfferProductId });
    void startPurchase(exitOfferProductId ?? undefined, "pro_annual_offer");
  };

  const handleDismissExitOffer = () => {
    setExitOfferOpen(false);
    capture("exit_offer_dismissed", { product_id: exitOfferProductId });
  };

  // Apple review requirement: previous store purchases must be re-linkable
  // (e.g. after a reinstall). Success lands on the same claim-polling page as
  // a purchase; if nothing was restored it times out gracefully there.
  const handleRestore = async () => {
    setBusy("restore");
    setError(null);
    try {
      const userId = await requireUserId();
      if (!userId) return;
      await restorePurchases(userId);
      navigate("/membership-success");
    } catch {
      setError("We couldn't restore any purchases for this account");
      setBusy(null);
    }
  };

  const footerLinkSx = {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: isDark ? "#9ca3af" : "#64748b",
    textDecoration: "none",
    textTransform: "none" as const,
    cursor: "pointer",
  };

  const planCards: { key: PaywallPackageKey; pkg: PaywallPackage | null }[] = [
    { key: "annual", pkg: annual },
    { key: "monthly", pkg: monthly },
  ];
  const showUnavailable = !packagesLoading && !annual && !monthly;

  return (
    <Box
      sx={{
        height: "100dvh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* The aurora sits behind a separate scroll container so it stays put
          while the pricing content scrolls. */}
      <AuroraGlow fullHeight />
      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          pt: "calc(env(safe-area-inset-top, 0px) + 20px)",
          pb: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
          px: 2.5,
          // iPad (and any future large surface) would otherwise stretch the
          // plan rows to absurd widths — hold the column and centre it.
          width: "100%",
          maxWidth: 560,
          mx: "auto",
        }}
      >
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box
          component="img"
          src={isDark ? "/insightLongLogoWhite.svg" : "/insightLongLogoPrimary.svg"}
          alt="Insight Analytics"
          sx={{ height: 24, width: "auto" }}
        />
      </Box>

      {/* Hierarchy: the personalized line IS the headline; the status line
          ("Your plan is ready") is the eyebrow above it. */}
      <Typography
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: isDark ? "#4ade80" : "#16a34a",
          ...heroRise(0),
        }}
      >
        {header.title}
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          fontWeight: 750,
          fontSize: "1.75rem",
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          color: isDark ? "#ffffff" : "#1d1d1f",
          ...heroRise(0.06),
        }}
      >
        {header.subtitle}
      </Typography>

      <Box sx={{ mb: 1.5 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Pro's perks, once — native sells a single tier, so they belong to
          the paywall rather than to either plan row. The detail line is a
          progressive enhancement: it drops on short screens so the plan rows
          never lose their space to it. */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          [TALL_SCREEN]: { mt: 2.5, gap: 1.25 },
          ...heroRise(0.18),
        }}
      >
        {NATIVE_PRO_FEATURES.map((feature) => (
          <Box key={feature.title} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <CheckRounded
              sx={{ fontSize: 16, color: "#22c55e", flexShrink: 0, mt: "2px" }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: isDark ? "#ffffff" : "#1d1d1f",
                  [TALL_SCREEN]: { fontSize: "0.875rem" },
                }}
              >
                {feature.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  lineHeight: 1.35,
                  color: isDark ? "#9ca3af" : "#64748b",
                  [SHORT_SCREEN]: { display: "none" },
                }}
              >
                {feature.detail}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Plan rows — this IS the decision, so they take the centre and the
          biggest type on the screen, and grow with the available height. */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", py: 1.5 }}>
      {packagesLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} sx={{ color: "#22c55e" }} />
        </Box>
      )}
      {showUnavailable && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => refetchPackages()}>
              Try again
            </Button>
          }
        >
          Plans aren&rsquo;t available right now.
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          // Wide enough that the yearly card's saving pill overhangs into
          // clear space rather than crowding what sits above it.
          gap: 2.25,
          [TALL_SCREEN]: { gap: 2.5 },
          ...heroRise(0.24),
        }}
      >
        {planCards.map(({ key, pkg }) => {
          if (!pkg) return null;
          const isSelected = pkg === selected;
          const isAnnual = key === "annual";
          return (
            <Box
              key={key}
              onClick={() => setSelectedKey(key)}
              sx={{
                p: 1.5,
                borderRadius: 3,
                cursor: "pointer",
                // Anchors the saving pill to the card's top border.
                position: "relative",
                [TALL_SCREEN]: { p: 1.875 },
                transition: "border-color 0.15s ease, background-color 0.15s ease",
                "@keyframes paywallCardPop": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.02)" },
                  "100%": { transform: "scale(1)" },
                },
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                border: isSelected
                  ? "1.5px solid #22c55e"
                  : `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                // Glass over the aurora, matching the onboarding cards.
                backgroundColor: isSelected
                  ? "rgba(34, 197, 94, 0.12)"
                  : isDark
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(255, 255, 255, 0.5)",
                backdropFilter: "blur(12px)",
                ...(isSelected && { animation: "paywallCardPop 0.25s ease" }),
              }}
            >
              {/* Saving pill, straddling the top border above the price
                  column so it reads as a tag ON the plan rather than a line
                  inside it. The percentage is derived from the two live store
                  prices: a hardcoded one silently lies the moment either
                  price moves. Flat fill, no shadow, uppercase (the design
                  system reserves caps for labels and badges); the lighter
                  green keeps it distinct from the selected card's #22c55e
                  without needing a cutout ring. */}
              {isAnnual && savingPercent > 0 && (
                <Typography
                  sx={{
                    position: "absolute",
                    // Centred on the border line, and `right` tracks the card
                    // padding so the pill's right edge lands in the same
                    // optical column as the price beneath it.
                    top: -12,
                    right: 12,
                    px: 0.875,
                    py: 0.25,
                    borderRadius: 999,
                    fontSize: "0.625rem",
                    fontWeight: 800,
                    lineHeight: 1.6,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    color: "#14532d",
                    backgroundColor: "#4ade80",
                    [TALL_SCREEN]: { right: 15 },
                  }}
                >
                  Save {savingPercent}%
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9375rem",
                      color: isDark ? "#ffffff" : "#1d1d1f",
                      [TALL_SCREEN]: { fontSize: "1rem" },
                    }}
                  >
                    {isAnnual && "Yearly"}
                    {!isAnnual && "Monthly"}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      mt: 0.25,
                      fontSize: "0.75rem",
                      color: isDark ? "#9ca3af" : "#64748b",
                      [TALL_SCREEN]: { fontSize: "0.8125rem" },
                    }}
                  >
                    {isAnnual && `billed ${pkg.priceString} a year`}
                    {!isAnnual && "billed monthly"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.1875rem",
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                      color: "#22c55e",
                      [TALL_SCREEN]: { fontSize: "1.3125rem" },
                    }}
                  >
                    {isAnnual && perMonthLabel(pkg)}
                    {!isAnnual && pkg.priceString}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        letterSpacing: 0,
                        color: isDark ? "#9ca3af" : "#64748b",
                        [TALL_SCREEN]: { fontSize: "0.8125rem" },
                      }}
                    >
                      {" "}
                      /mo
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      </Box>

      <Button
        fullWidth
        disabled={busy !== null || !selected}
        onClick={handlePurchase}
        sx={{
          mt: 2,
          py: 1.2,
          backgroundColor: "#22c55e",
          color: "#ffffff",
          fontWeight: 600,
          fontSize: "0.9375rem",
          textTransform: "none",
          borderRadius: 2,
          "&:hover": { backgroundColor: "#16a34a" },
          "&:disabled": {
            backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
          },
        }}
      >
        {busy === "purchase" && <CircularProgress size={22} color="inherit" />}
        {busy !== "purchase" && trialDays !== null && `Start ${trialDays}-day free trial`}
        {busy !== "purchase" && trialDays === null && "Subscribe"}
      </Button>

      {/* Auto-renewal disclosure (App Store 3.1.2 subscription requirements):
          the charge after the trial, and the term it recurs on, in store
          figures. */}
      <Typography
        sx={{
          mt: 1.5,
          textAlign: "center",
          fontSize: "0.6875rem",
          lineHeight: 1.5,
          color: isDark ? "#6b7280" : "#9ca3af",
        }}
      >
        {selected && trialDays !== null && (
          `${trialDays} days free, then ${selected.priceString} ${renewalTerm}. `
        )}
        {selected && trialDays === null && (
          `${selected.priceString} ${renewalTerm}. `
        )}
        Auto-renews until cancelled. Cancel anytime in your {storeName} settings.
      </Typography>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Link component="button" onClick={handleRestore} disabled={busy !== null} sx={footerLinkSx}>
          {busy === "restore" ? "Restoring…" : "Restore purchases"}
        </Link>
        <Link component="button" onClick={() => navigate("/terms")} sx={footerLinkSx}>
          Terms
        </Link>
        <Link component="button" onClick={() => navigate("/privacy-policy")} sx={footerLinkSx}>
          Privacy
        </Link>
        <Link component="button" onClick={() => logout()} sx={footerLinkSx}>
          Sign out
        </Link>
      </Box>
      </Box>

      {offerStorePricing && offerIntro && monthly && (
        <ExitOfferSheet
          open={exitOfferOpen}
          busy={busy === "purchase"}
          perMonthPrice={formatPrice(offerIntro.price / 12, offerStorePricing.currencyCode)}
          standardPerMonthPrice={formatPrice(
            offerStorePricing.price / 12,
            offerStorePricing.currencyCode,
          )}
          introTotal={offerIntro.priceString}
          renewalTotal={offerStorePricing.priceString}
          savingPercent={annualSavingPercent(monthly.price, offerIntro.price)}
          onAccept={handleAcceptExitOffer}
          onDismiss={handleDismissExitOffer}
        />
      )}
    </Box>
  );
};

export default NativePaywall;
